import React, { useState } from 'react';
import { useCookieConsent } from '../context/CookieConsentContext';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const CookieConsentBanner = () => {
  const { t } = useLanguage();
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  
  const [localConsent, setLocalConsent] = useState({
    necessary: true,
    performance: false,
    functional: false,
    marketing: false,
  });

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleRejectAll = () => {
    rejectAll();
  };

  const handleToggleSetting = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    
    setLocalConsent(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSavePreferences = () => {
    savePreferences(localConsent);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-stone-900 border-t-2 border-gray-200 dark:border-stone-800 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Simple View */}
        {!showSettings && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <i className="fas fa-cookie-bite text-orange-500 mr-2 text-2xl"></i>
                <h3 className="text-lg font-display font-bold text-gray-900 dark:text-stone-100">
                  {t('cookieUsageTitle')}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-stone-300 leading-relaxed">
                {t('cookieUsageDescPrefix')}{' '}
                <Link to="/cookie-policy" className="text-accent-orange hover:underline font-medium">
                  {t('cookiePolicy')}
                </Link>{' '}
                {t('cookieUsageDescSuffix')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-gray-300 dark:border-stone-700 rounded-lg hover:bg-gray-50 dark:hover:bg-stone-700 transition-colors"
              >
                <i className="fas fa-cog mr-2"></i>
                {t('settings')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-stone-300 bg-gray-100 dark:bg-stone-800 border border-gray-300 dark:border-stone-700 rounded-lg hover:bg-gray-200 dark:hover:bg-stone-700 transition-colors"
              >
                {t('onlyNecessary')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 text-sm font-medium text-white bg-accent-orange rounded-lg hover:bg-orange-600 transition-colors"
              >
                {t('acceptAll')}
              </button>
            </div>
          </div>
        )}

        {/* Settings View */}
        {showSettings && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-bold text-gray-900 dark:text-stone-100 mb-2">
                {t('cookiePreferencesTitle')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-stone-300">
                {t('cookiePreferencesDesc')}
              </p>
            </div>

            {/* Necessary Cookies */}
            <div className="flex items-start justify-between p-4 bg-green-50 dark:bg-green-950/40 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <i className="fas fa-shield-alt text-green-600 mr-2"></i>
                  <h4 className="font-display font-semibold text-gray-900 dark:text-stone-100">
                    {t('necessaryCookiesTitle')}
                  </h4>
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-300 rounded">
                    {t('alwaysActive')}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-stone-300">
                  {t('necessaryCookiesDesc')}
                </p>
              </div>
              <div className="ml-4">
                <input
                  type="checkbox"
                  checked={localConsent.necessary}
                  disabled
                  className="w-5 h-5 rounded border-gray-300 dark:border-stone-600 text-green-600 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Performance Cookies */}
            <div className="flex items-start justify-between p-4 bg-white dark:bg-stone-800 rounded-lg border border-gray-300 dark:border-stone-700">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <i className="fas fa-chart-line text-blue-600 mr-2"></i>
                  <h4 className="font-display font-semibold text-gray-900 dark:text-stone-100">
                    {t('performanceCookiesTitle')}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-stone-300 mb-2">
                  {t('performanceCookiesDesc')}
                </p>
                <p className="text-xs text-gray-500 dark:text-stone-400">
                  {t('examplesLabel')}: Google Analytics
                </p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConsent.performance}
                    onChange={() => handleToggleSetting('performance')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-stone-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-orange"></div>
                </label>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="flex items-start justify-between p-4 bg-white dark:bg-stone-800 rounded-lg border border-gray-300 dark:border-stone-700">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <i className="fas fa-cogs text-purple-600 mr-2"></i>
                  <h4 className="font-display font-semibold text-gray-900 dark:text-stone-100">
                    {t('functionalCookiesTitle')}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-stone-300 mb-2">
                  {t('functionalCookiesDesc')}
                </p>
                <p className="text-xs text-gray-500 dark:text-stone-400">
                  {t('examplesLabel')}: {t('functionalCookiesExamples')}
                </p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConsent.functional}
                    onChange={() => handleToggleSetting('functional')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-stone-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-orange"></div>
                </label>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between p-4 bg-white dark:bg-stone-800 rounded-lg border border-gray-300 dark:border-stone-700">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <i className="fas fa-bullhorn text-red-600 mr-2"></i>
                  <h4 className="font-display font-semibold text-gray-900 dark:text-stone-100">
                    {t('marketingCookiesTitle')}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-stone-300 mb-2">
                  {t('marketingCookiesDesc')}
                </p>
                <p className="text-xs text-gray-500 dark:text-stone-400">
                  {t('examplesLabel')}: Google Ads, Facebook Pixel
                </p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConsent.marketing}
                    onChange={() => handleToggleSetting('marketing')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-stone-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-orange"></div>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-stone-800">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-6 py-3 text-sm font-medium text-white bg-accent-orange rounded-lg hover:bg-orange-600 transition-colors"
              >
                <i className="fas fa-save mr-2"></i>
                {t('saveMyPreferences')}
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-stone-300 bg-gray-100 dark:bg-stone-800 rounded-lg hover:bg-gray-200 dark:hover:bg-stone-700 transition-colors"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;

